import {
  WIDGETS,
  boxForNew,
  loadDashboardConfig,
  saveDashboardConfig,
  uniqueName,
  widgetLabel,
} from "../orion/config/dashboards";

/**
 * The dashboards, as operations rather than as a screen.
 * ---------------------------------------------------------------------------
 * Everything the boards are is in one saved object: a list of dashboards, each
 * a name and a list of widgets, each widget an id out of a fixed catalogue and
 * the box it sits in. That is a thing a model can be given honestly - it is
 * small, it is named, and every id in it is one of about thirty real ones.
 *
 * So these tools write that object and let the page redraw from it, which is
 * exactly what the board's own edit mode does. Nothing here drives a cursor
 * around a canvas.
 *
 * The one piece of judgement is `about`: a board asked for in words ("forge
 * stats, assessments and vulnerabilities") is matched against the catalogue's
 * own groups and labels. A model that cannot remember thirty widget ids can
 * still ask for the right board, and what it gets back says exactly which
 * widgets were put on it - so the next step can correct it.
 * ---------------------------------------------------------------------------
 */

/* How many widgets a board asked for in words is allowed to collect. A board
   is something to look at; forty cards is a document. */
const MOST = 12;

const idsOf = (list) => list.map(w => w.id);

/** The catalogue, as something short enough to put in a prompt. */
function catalogue() {
  return WIDGETS.map(w => ({
    id: w.id,
    label: w.label,
    group: w.group || w.label,
    ...(w.variant ? { as: w.variant } : {}),
    ...(w.forge ? { needs: "forge" } : {}),
  }));
}

/* Words to the widgets they mean. Matched against the id, the label and the
   group, which is where the product's own vocabulary already lives: "forge"
   finds the eleven Forge widgets because they are the ones that say forge. */
/**
 * A title out of what was asked for.
 *
 * Not the sentence itself - "all the forge stats, assessment and vulnerability
 * statuses" is a description, not a name, and a tab is about twenty characters
 * wide. The words that carry it, capitalised, and no more than four of them.
 */
function titleFrom(about) {
  const SKIP = ["the", "all", "and", "for", "with", "new", "page", "dashboard", "show", "me", "a", "of", "statuses", "status", "that", "will", "contain"];
  const words = String(about || "").toLowerCase().split(/[^a-z0-9]+/)
    .filter(w => w.length > 2 && !SKIP.includes(w));
  if (!words.length) return "";
  return words.slice(0, 4).map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
}

function widgetsAbout(words) {
  const terms = String(words || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(w => w.length > 2 && !["the", "all", "and", "for", "with", "new", "page", "stats", "status", "statuses", "dashboard"].includes(w));
  if (!terms.length) return [];

  /* a widget counts if any of the words is in what it is called */
  const hit = (w) => {
    const hay = `${w.id} ${w.label} ${w.group || ""}`.toLowerCase();
    return terms.some(t => hay.includes(t) || (t.endsWith("s") && hay.includes(t.slice(0, -1))));
  };

  /* One per group: the catalogue carries the same measurement drawn three ways
     (outcomes as a figure, a ring and an orbit), and a board with all three on
     it is the same thing three times. The first of a group is the one the
     product itself lists first. */
  const seen = new Set();
  const out = [];
  for (const w of WIDGETS) {
    if (!hit(w)) continue;
    const group = w.group || w.label;
    if (seen.has(group)) continue;
    seen.add(group);
    out.push(w);
    if (out.length >= MOST) break;
  }
  return out;
}

/** A widget placed on a board, with the box a new one gets. */
function place(widget, onBoard) {
  const span = widget.span || 4;
  return { id: widget.id, span, ...(boxForNew(onBoard, span, 0, widget.id) || {}) };
}

export function dashboardTools(say, go) {
  const read = async () => {
    const config = await loadDashboardConfig();
    return { config, dashboards: config?.dashboards || [] };
  };

  const find = (dashboards, which) => {
    const q = String(which || "").trim().toLowerCase();
    if (!q) return null;
    return dashboards.find(d => d.id === q)
      || dashboards.find(d => (d.name || "").toLowerCase() === q)
      || dashboards.find(d => (d.name || "").toLowerCase().includes(q))
      || null;
  };

  return [
    {
      name: "list_dashboards",
      description:
        "The dashboard pages that exist, with the widgets on each. Call it before changing one, "
        + "and to find the id that the other dashboard tools take.",
      inputSchema: { type: "object", properties: {} },
      async execute() {
        const { config, dashboards } = await read();
        return say(JSON.stringify({
          open: config?.activeId || null,
          dashboards: dashboards.map(d => ({
            id: d.id,
            name: d.name,
            widgets: idsOf(d.widgets || []),
          })),
        }, null, 2));
      },
    },

    {
      name: "list_widgets",
      description:
        "Every widget a dashboard page can show, by id, with the group it belongs to and how it is drawn. "
        + "These ids are the only ones add_widget and create_dashboard accept.",
      inputSchema: {
        type: "object",
        properties: { about: { type: "string", description: "Optional. Only widgets matching these words." } },
      },
      async execute({ about } = {}) {
        const all = catalogue();
        const list = about ? catalogue().filter(w => idsOf(widgetsAbout(about)).includes(w.id)) : all;
        return say(JSON.stringify({ widgets: list }, null, 2));
      },
    },

    {
      name: "create_dashboard",
      description:
        "Make a new dashboard page. Say what it is about in words - \"forge, assessments and vulnerabilities\" - "
        + "and the widgets that match are put on it; or give exact widget ids. "
        + "Returns the board with the widgets it ended up with, and opens it.",
      inputSchema: {
        type: "object",
        properties: {
          name: { type: "string", description: "What to call it." },
          about: { type: "string", description: "What it should show, in words." },
          widgets: {
            type: "array",
            items: { type: "string" },
            description: "Or exact widget ids from list_widgets.",
          },
        },
        required: [],
      },
      async execute({ name, about, widgets } = {}) {
        const { config, dashboards } = await read();

        const asked = Array.isArray(widgets) && widgets.length
          ? WIDGETS.filter(w => widgets.includes(w.id))
          : widgetsAbout(about || name);

        if (!asked.length) {
          return say(JSON.stringify({
            made: null,
            why: "nothing in the catalogue matches that - call list_widgets and give ids",
          }, null, 2));
        }

        /* A name is nice to have and not worth refusing over: a request that
           says what the page is for has already said enough to title it, so
           an untitled board is named after the first thing on it rather than
           coming out called "undefined". */
        const called = String(name || "").trim()
          || titleFrom(about)
          || `${widgetLabel(asked[0].id)} board`;

        const board = {
          id: `d${Date.now().toString(36)}`,
          name: uniqueName(called.slice(0, 60), dashboards.map(d => d.name)),
          widgets: [],
        };
        asked.forEach(w => { board.widgets.push(place(w, board.widgets)); });

        await saveDashboardConfig({ ...config, dashboards: [...dashboards, board], activeId: board.id });
        go("/dashboard");

        return say(JSON.stringify({
          made: { id: board.id, name: board.name },
          widgets: board.widgets.map(w => ({ id: w.id, label: widgetLabel(w.id) })),
          note: "It is open now. Add or remove widgets with add_widget and remove_widget.",
        }, null, 2));
      },
    },

    {
      name: "add_widget",
      description: "Put another widget on a dashboard page. Takes a widget id from list_widgets.",
      inputSchema: {
        type: "object",
        properties: {
          widget: { type: "string", description: "The widget id." },
          dashboard: { type: "string", description: "Which board. The open one if left out." },
        },
        required: ["widget"],
      },
      async execute({ widget, dashboard } = {}) {
        const { config, dashboards } = await read();
        const board = find(dashboards, dashboard) || dashboards.find(d => d.id === config?.activeId) || dashboards[0];
        if (!board) return say("There are no dashboard pages yet - create_dashboard makes one.");

        const found = WIDGETS.find(w => w.id === widget);
        if (!found) return say(`No widget called "${widget}". Call list_widgets for the ids.`);
        if ((board.widgets || []).some(w => w.id === widget)) {
          return say(`"${widgetLabel(widget)}" is already on ${board.name}.`);
        }

        const next = dashboards.map(d => (d.id === board.id
          ? { ...d, widgets: [...(d.widgets || []), place(found, d.widgets || [])] }
          : d));
        await saveDashboardConfig({ ...config, dashboards: next, activeId: board.id });
        go("/dashboard");
        return say(JSON.stringify({ added: widgetLabel(widget), to: board.name }, null, 2));
      },
    },

    {
      name: "remove_widget",
      description: "Take a widget off a dashboard page.",
      inputSchema: {
        type: "object",
        properties: {
          widget: { type: "string", description: "The widget id." },
          dashboard: { type: "string", description: "Which board. The open one if left out." },
        },
        required: ["widget"],
      },
      async execute({ widget, dashboard } = {}) {
        const { config, dashboards } = await read();
        const board = find(dashboards, dashboard) || dashboards.find(d => d.id === config?.activeId) || dashboards[0];
        if (!board) return say("There are no dashboard pages.");
        if (!(board.widgets || []).some(w => w.id === widget)) {
          return say(`"${widget}" is not on ${board.name}.`);
        }

        const next = dashboards.map(d => (d.id === board.id
          ? { ...d, widgets: (d.widgets || []).filter(w => w.id !== widget) }
          : d));
        await saveDashboardConfig({ ...config, dashboards: next, activeId: board.id });
        go("/dashboard");
        return say(JSON.stringify({ removed: widgetLabel(widget), from: board.name }, null, 2));
      },
    },

    {
      name: "delete_dashboard",
      description: "Delete a dashboard page and everything on it. Name it exactly - this cannot be undone.",
      inputSchema: {
        type: "object",
        properties: { dashboard: { type: "string", description: "Its name or its id." } },
        required: ["dashboard"],
      },
      async execute({ dashboard } = {}) {
        const { config, dashboards } = await read();
        const board = find(dashboards, dashboard);
        if (!board) return say(`There is no dashboard called "${dashboard}".`);
        if (dashboards.length < 2) return say("That is the only dashboard there is, so it stays.");

        const left = dashboards.filter(d => d.id !== board.id);
        await saveDashboardConfig({
          ...config,
          dashboards: left,
          activeId: config.activeId === board.id ? left[0].id : config.activeId,
        });
        go("/dashboard");
        return say(`Deleted "${board.name}". ${left.length} dashboard${left.length === 1 ? "" : "s"} left.`);
      },
    },

    {
      name: "rename_dashboard",
      description: "Give a dashboard page a different name.",
      inputSchema: {
        type: "object",
        properties: {
          dashboard: { type: "string", description: "Its name or its id." },
          name: { type: "string", description: "What to call it instead." },
        },
        required: ["dashboard", "name"],
      },
      async execute({ dashboard, name } = {}) {
        const { config, dashboards } = await read();
        const board = find(dashboards, dashboard);
        if (!board) return say(`There is no dashboard called "${dashboard}".`);
        const called = uniqueName(String(name || "").trim().slice(0, 60), dashboards.filter(d => d.id !== board.id).map(d => d.name));
        if (!called) return say("A dashboard needs a name.");

        await saveDashboardConfig({
          ...config,
          dashboards: dashboards.map(d => (d.id === board.id ? { ...d, name: called } : d)),
        });
        return say(`"${board.name}" is now "${called}".`);
      },
    },

    {
      name: "open_dashboard",
      description: "Show one of the dashboard pages.",
      inputSchema: {
        type: "object",
        properties: { dashboard: { type: "string", description: "Its id or its name." } },
        required: ["dashboard"],
      },
      async execute({ dashboard } = {}) {
        const { config, dashboards } = await read();
        const board = find(dashboards, dashboard);
        if (!board) {
          return say(JSON.stringify({ opened: null, there_are: dashboards.map(d => d.name) }, null, 2));
        }
        await saveDashboardConfig({ ...config, activeId: board.id });
        go("/dashboard");
        return say(JSON.stringify({ opened: board.name, widgets: idsOf(board.widgets || []) }, null, 2));
      },
    },
  ];
}

export default dashboardTools;
